/**
 * Grade Matrix Generator Component
 * 
 * Generates and displays peer grading and self-eval matrix messages
 * based on local grade state. Replaces backend matrix generation.
 */

import { useEffect, useRef, useState } from 'react';
import { LocalGradeProcessor, ConversationGradeState, RawGrade } from '../../services/localGradeProcessor';
import { SelfEvalConfigService } from '../../services/selfEvalConfigService';
import { useSelfGrade } from '../../hooks/useSelfGrade';
import { Message } from '../../types';

interface GradeMatrixGeneratorProps {
  localGradeState: ConversationGradeState | null;
  messages: Message[];
  gradeProcessor: LocalGradeProcessor;
  onAddMessage: (message: Message) => void;
  txtEnabled?: boolean;
}

export const GradeMatrixGenerator: React.FC<GradeMatrixGeneratorProps> = ({
  localGradeState,
  messages,
  gradeProcessor,
  onAddMessage,
  txtEnabled = false // This is the current ChatLayout state, may not match conversation
}) => {
  // 🎯 FIX: Track per-PROMPT matrix generation, not per-conversation
  // This allows concurrent prompts in the same conversation to each get their matrix
  const peerMatrixGeneratedPromptsRef = useRef<Set<string>>(new Set());
  const selfEvalMatrixGeneratedPromptsRef = useRef<Set<string>>(new Set());
  const lastPeerGradeCountRef = useRef(0);
  const lastSelfEvalGradeCountRef = useRef(0);
  const lastConversationIdRef = useRef<string | null>(null);
  
  // 🎯 NEW: Fetch self-eval grader count from config
  const [configuredGraderCount, setConfiguredGraderCount] = useState<number | null>(null);
  
  // 🎯 NEW: Get selfgrade system setting
  const { selfgrade: selfgradeEnabled } = useSelfGrade();
  
  useEffect(() => {
    // Fetch self-eval config on mount
    SelfEvalConfigService.getConfig().then(config => {
      if (config?.number) {
        setConfiguredGraderCount(config.number);
        console.log('📊 Self-eval grader count from config:', config.number);
      }
    });
  }, []);

  useEffect(() => {
    if (!localGradeState) {
      return;
    }

    // Async logic wrapped in IIFE since useEffect cannot be async directly
    (async () => {

    // CRITICAL: Validate that localGradeState matches the current conversation
    // Get conversationId from first grade (all grades in state belong to same conversation)
    const stateConversationId = localGradeState.conversationId;
    const gradeConversationId = localGradeState.rawGrades[0]?.conversationId || null;
    const currentConversationId = gradeConversationId;
    
    // Cross-validate with messages to get the actual current conversation
    // Use userPromptMessageId from grades to find the prompt message
    const userPromptId = localGradeState.rawGrades[0]?.userPromptMessageId;
    const userPromptMessage = messages.find(m => m.id === userPromptId);
    const actualConversationId = userPromptMessage ? stateConversationId : null;
    
    // If localGradeState is stale (from previous conversation), ignore it
    // We validate by checking if the user prompt message exists in current messages
    if (userPromptId && !userPromptMessage) {
      console.log('⚠️ GradeMatrixGenerator: Ignoring stale local state', {
        stateConversationId: stateConversationId,
        userPromptId: userPromptId,
        userPromptFound: false
      });
      return;
    }

    // Reset flags when conversation changes
    if (currentConversationId !== lastConversationIdRef.current) {
      console.log('🔄 Conversation changed, resetting matrix generation flags', {
        from: lastConversationIdRef.current,
        to: currentConversationId
      });
      // 🎯 FIX: Clear the per-prompt tracking sets when switching conversations
      peerMatrixGeneratedPromptsRef.current.clear();
      selfEvalMatrixGeneratedPromptsRef.current.clear();
      lastPeerGradeCountRef.current = 0;
      lastSelfEvalGradeCountRef.current = 0;
      lastConversationIdRef.current = currentConversationId;
    }
    
    // CRITICAL: For peer grades, determine txtEnabled and model count from the actual 
    // prompt message that generated these grades, not from the current ChatLayout state
    // (userPromptId and userPromptMessage already declared above for validation)
    const actualTxtEnabled = userPromptMessage?.sendConfig?.txt ?? false;
    const modelCount = userPromptMessage?.sendConfig?.ais ?? 0;

    const peerGrades = localGradeState.rawGrades.filter(g => !g.isSelfEval);
    const selfEvalGrades = localGradeState.rawGrades.filter(g => g.isSelfEval);

    // 🎯 FIX: Group peer grades by userPromptMessageId for concurrent prompts
    const peerGradesByPrompt = new Map<string, typeof peerGrades>();
    peerGrades.forEach(g => {
      const promptId = g.userPromptMessageId || 'unknown';
      if (!peerGradesByPrompt.has(promptId)) {
        peerGradesByPrompt.set(promptId, []);
      }
      peerGradesByPrompt.get(promptId)!.push(g);
    });

    // Check if matrix already exists in messages (for old conversations)
    const hasPeerMatrix = messages.some(m => m.isGradeMatrix && m.content.includes('📊 Peer Grading Matrix'));
    const hasSelfEvalMatrix = messages.some(m => m.isGradeMatrix && m.content.includes('📊 Self-Eval Grading Matrix'));
    
    // Debug: Log all grade matrix messages
    const gradeMatrixMessages = messages.filter(m => m.isGradeMatrix);
    if (gradeMatrixMessages.length > 0) {
      console.log('🔍 Found grade matrix messages:', {
        count: gradeMatrixMessages.length,
        messages: gradeMatrixMessages.map(m => ({
          id: m.id,
          contentPreview: m.content.substring(0, 50),
          isGradeMatrix: m.isGradeMatrix
        }))
      });
    }

    // Debug logging for peer grades
    if (peerGrades.length > 0 && peerGrades.length !== lastPeerGradeCountRef.current) {
      console.log('🔍 Peer grade state check:', {
        peerGradeCount: peerGrades.length,
        lastCount: lastPeerGradeCountRef.current,
        promptsGenerated: peerMatrixGeneratedPromptsRef.current.size,
        matrixExists: hasPeerMatrix,
        uniqueGraders: new Set(peerGrades.map(g => g.graderModelId)).size,
        uniqueCandidates: new Set(peerGrades.map(g => g.candidateModelId)).size
      });
    }

    // 🎯 FIX: Generate peer grading matrix PER PROMPT for concurrent conversations
    // Iterate over each prompt's grades separately
    for (const [promptId, promptPeerGrades] of Array.from(peerGradesByPrompt.entries())) {
      // Skip if already generated for this prompt
      if (peerMatrixGeneratedPromptsRef.current.has(promptId)) {
        continue;
      }
      
      // Get the user message for this prompt to determine settings
      const promptUserMessage = messages.find(m => m.id === promptId);
      if (!promptUserMessage) {
        console.log('⚠️ Could not find user message for prompt', { promptId });
        continue;
      }
      
      const promptTxtEnabled = promptUserMessage.sendConfig?.txt ?? false;
      const promptModelCount = promptUserMessage.sendConfig?.ais ?? 0;
      // Use the selfGradeEnabled setting from when this prompt was sent
      const promptSelfGradeEnabled = promptUserMessage.sendConfig?.selfGradeEnabled ?? false;
      
      const uniqueGraders = new Set(promptPeerGrades.map((g: RawGrade) => g.graderModelId)).size;
      const uniqueCandidates = new Set(promptPeerGrades.map((g: RawGrade) => g.candidateModelId)).size;
      
      // Calculate expected grade count for THIS prompt
      // With self-grading: each grader also grades themselves → +N grades
      let expectedTxtGrades = 0;
      if (promptTxtEnabled && promptModelCount > 0) {
        // TxT mode: Each model grades all others (+ self if enabled)
        // Without self-grading: N × (N-1) grades
        // With self-grading: N × N grades
        expectedTxtGrades = promptSelfGradeEnabled 
          ? promptModelCount * promptModelCount 
          : promptModelCount * (promptModelCount - 1);
      }
      
      // Progressive mode: Calculate expected grades using triangle formula
      let expectedProgressiveGrades = 0;
      if (!promptTxtEnabled && promptModelCount > 0) {
        const N = promptModelCount;
        const remainder = N % 3;
        // Base formula without self-grading: 2N, 2N+2, or 2N+4
        // With self-grading: add N (one self-grade per grader) → 3N, 3N+2, or 3N+4
        const baseMultiplier = promptSelfGradeEnabled ? 3 : 2;
        if (remainder === 0) {
          expectedProgressiveGrades = baseMultiplier * N;
        } else if (remainder === 1) {
          expectedProgressiveGrades = baseMultiplier * N + 4;
        } else {
          expectedProgressiveGrades = baseMultiplier * N + 2;
        }
      }
      
      // Check if we have all expected grades for THIS prompt
      const hasAllTxtGrades = promptPeerGrades.length >= expectedTxtGrades && expectedTxtGrades > 0;
      const hasAllProgressiveGrades = promptPeerGrades.length >= expectedProgressiveGrades && 
                                       expectedProgressiveGrades > 0;
      
      // Check if matrix already exists for this prompt (content matching)
      const promptHasMatrix = messages.some(m => 
        m.isGradeMatrix && 
        m.content.includes('📊 Peer Grading Matrix') &&
        (m as any).userPromptMessageId === promptId
      );
      
      // Determine if we should generate for this prompt
      const shouldGenerateTxt = promptTxtEnabled && 
                                hasAllTxtGrades &&
                                !promptHasMatrix;
      
      const shouldGenerateNormal = !promptTxtEnabled && 
                                   hasAllProgressiveGrades &&
                                   !promptHasMatrix;
      
      if (shouldGenerateTxt || shouldGenerateNormal) {
        // CRITICAL: Set flag IMMEDIATELY to prevent duplicate generations
        peerMatrixGeneratedPromptsRef.current.add(promptId);
        
        const generationReason = shouldGenerateTxt 
          ? 'TxT mode - all grades received'
          : 'Progressive mode - all expected grades received';
        
        console.log('📊 Generating peer grading matrix from frontend', {
          promptId,
          peerGrades: promptPeerGrades.length,
          graders: uniqueGraders,
          candidates: uniqueCandidates,
          txtEnabled: promptTxtEnabled,
          modelCount: promptModelCount,
          expectedTxtGrades,
          expectedProgressiveGrades,
          hasAllTxtGrades,
          hasAllProgressiveGrades,
          reason: generationReason,
        });

        const matrixContent = await gradeProcessor.generatePeerGradingMatrix(
          promptPeerGrades,
          messages,
          selfgradeEnabled
        );

        if (matrixContent) {
          const lastGradeTimestamp = promptPeerGrades.length > 0
            ? promptPeerGrades[promptPeerGrades.length - 1].timestamp
            : new Date();
          
          const matrixMessage: Message = {
            id: `peer-matrix-${promptId}-${Date.now()}`,
            role: 'assistant',
            content: matrixContent,
            timestamp: lastGradeTimestamp,
            modelUsed: 'ionoff',
            isGradeMatrix: true,
            costUSD: 0,
            conversationId: currentConversationId || undefined,
            userPromptMessageId: promptId, // 🎯 Link to prompt for proper grouping
          } as Message & { conversationId?: string; userPromptMessageId?: string };
          
          console.log('📤 Adding peer matrix message to chat:', {
            messageId: matrixMessage.id,
            promptId,
            gradeCount: promptPeerGrades.length,
            contentLength: matrixMessage.content.length,
            mode: promptTxtEnabled ? 'TxT (final)' : 'Progressive (final)'
          });
          
          onAddMessage(matrixMessage);
          console.log('✅ Peer grading matrix added to chat for prompt', promptId);
        }
      }
    }
    
    // Update last count (for backwards compat / logging)
    lastPeerGradeCountRef.current = peerGrades.length;

    // 🎯 FIX: Group self-eval grades by userPromptMessageId for concurrent prompts
    const selfEvalGradesByPrompt = new Map<string, typeof selfEvalGrades>();
    selfEvalGrades.forEach(g => {
      const promptId = g.userPromptMessageId || 'unknown';
      if (!selfEvalGradesByPrompt.has(promptId)) {
        selfEvalGradesByPrompt.set(promptId, []);
      }
      selfEvalGradesByPrompt.get(promptId)!.push(g);
    });
    
    // Process self-eval matrices PER PROMPT
    for (const [promptId, promptSelfEvalGrades] of Array.from(selfEvalGradesByPrompt.entries())) {
      // Skip if already generated for this prompt
      if (selfEvalMatrixGeneratedPromptsRef.current.has(promptId)) {
        continue;
      }
      
      // 🎯 FIX: Calculate expected grade count using config + synthesis variant count
      // expectedGrades = configuredGraderCount × (1 + synthesisVariantCount)
      // - 1 = peer review winner ("best")
      // - synthesisVariantCount = number of synthesis variants for this prompt
      const synthesisVariantsForPrompt = messages.filter(m => 
        m.role === 'assistant' && 
        m.synthesisVariant && 
        (m as any).userPromptMessageId === promptId
      ).length;
      
      // Debug: Log all synthesis messages to help diagnose issues
      const allSynthesisMessages = messages.filter(m => m.role === 'assistant' && (m.synthesisVariant || m.isSynthesized));
      if (promptSelfEvalGrades.length > 0 && allSynthesisMessages.length > 0) {
        console.log('🔍 Synthesis messages for self-eval expected count:', {
          promptId,
          totalSynthesisMessages: allSynthesisMessages.length,
          matchingPrompt: synthesisVariantsForPrompt,
          synthesisDetails: allSynthesisMessages.map(m => ({
            id: m.id,
            synthesisVariant: (m as any).synthesisVariant,
            isSynthesized: m.isSynthesized,
            userPromptMessageId: (m as any).userPromptMessageId,
            matchesPrompt: (m as any).userPromptMessageId === promptId,
          }))
        });
      }
      
      const candidateCount = 1 + synthesisVariantsForPrompt; // best + synthesis variants
      const expectedSelfEvalGrades = configuredGraderCount 
        ? configuredGraderCount * candidateCount
        : 0;
      
      // Fallback: If config not loaded yet, use derived count from grades
      const uniqueGraders = new Set(promptSelfEvalGrades.map((g: RawGrade) => g.graderModelId)).size;
      const uniqueCandidates = new Set(promptSelfEvalGrades.map((g: RawGrade) => g.candidateModelId)).size;
      const derivedExpectedGrades = uniqueGraders > 0 && uniqueCandidates > 0 
        ? uniqueGraders * uniqueCandidates
        : 0;
      
      // Use config-based expected if available, otherwise derived
      const finalExpectedGrades = expectedSelfEvalGrades > 0 
        ? expectedSelfEvalGrades 
        : derivedExpectedGrades;
      
      // Check if matrix already exists for this prompt
      const promptHasMatrix = messages.some(m => 
        m.isGradeMatrix && 
        m.content?.includes('📊 Self-Eval Grading Matrix') &&
        (m as any).userPromptMessageId === promptId
      );
      
      // Debug logging
      if (promptSelfEvalGrades.length > 0) {
        const willGenerate = promptSelfEvalGrades.length >= finalExpectedGrades && 
                            finalExpectedGrades > 0 && 
                            !promptHasMatrix &&
                            uniqueGraders >= 2 && 
                            uniqueCandidates >= 2;
        console.log('🔍 Self-eval matrix generation check:', {
          promptId,
          selfEvalGradeCount: promptSelfEvalGrades.length,
          expectedGrades: finalExpectedGrades,
          derivedExpected: derivedExpectedGrades,
          configExpected: expectedSelfEvalGrades,
          configuredGraders: configuredGraderCount,
          uniqueGraders,
          uniqueCandidates,
          synthesisVariants: synthesisVariantsForPrompt,
          candidateCount,
          alreadyGenerated: selfEvalMatrixGeneratedPromptsRef.current.has(promptId),
          matrixExists: promptHasMatrix,
          willGenerate,
        });
      }

      // 🎯 FIX: Generate matrix when we have ALL expected grades (no cost message check)
      // The grade count itself is the signal that self-eval is complete
      if (promptSelfEvalGrades.length > 0 && 
          promptSelfEvalGrades.length >= finalExpectedGrades &&
          finalExpectedGrades > 0 &&
          !promptHasMatrix) {
        
        // Generate matrix when we have at least 2 graders and 2 candidates
        if (uniqueGraders >= 2 && uniqueCandidates >= 2) {
          // Set flag IMMEDIATELY to prevent duplicates
          selfEvalMatrixGeneratedPromptsRef.current.add(promptId);
          
          console.log('📊 Generating self-eval matrix from frontend', {
            promptId,
            selfEvalGrades: promptSelfEvalGrades.length,
            expectedGrades: expectedSelfEvalGrades,
            graders: uniqueGraders,
            candidates: uniqueCandidates,
          });

          const matrixContent = await gradeProcessor.generateSelfEvalMatrix(
            promptSelfEvalGrades,
            messages
          );

          if (matrixContent) {
            const firstGradeTimestamp = promptSelfEvalGrades.length > 0 
              ? promptSelfEvalGrades[0].timestamp 
              : new Date();
            
            const matrixMessage: Message = {
              id: `self-eval-matrix-${promptId}-${Date.now()}`,
              role: 'assistant',
              content: matrixContent,
              timestamp: firstGradeTimestamp,
              modelUsed: 'ionoff',
              isGradeMatrix: true,
              costUSD: 0,
              conversationId: currentConversationId || undefined,
              userPromptMessageId: promptId, // 🎯 Link to prompt for proper grouping
            } as Message & { conversationId?: string; userPromptMessageId?: string };
            
            console.log('🎯 Adding self-eval matrix to chat', {
              messageId: matrixMessage.id,
              promptId,
              contentLength: matrixContent.length,
            });
            
            onAddMessage(matrixMessage);
            console.log('✅ Self-eval matrix added to chat for prompt', promptId);
          } else {
            console.warn('⚠️ Self-eval matrix generation returned empty content');
          }
        }
      } else if (promptHasMatrix && !selfEvalMatrixGeneratedPromptsRef.current.has(promptId)) {
        // Mark as generated if it already exists in messages
        selfEvalMatrixGeneratedPromptsRef.current.add(promptId);
      }
    }
    
    // Update last count for logging
    lastSelfEvalGradeCountRef.current = selfEvalGrades.length;
    })(); // End of async IIFE
  }, [localGradeState, messages, gradeProcessor, onAddMessage]);

  // This component doesn't render anything - it just generates matrices
  return null;
};

